import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import { Plus, SquarePen, Trash2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllPressNewsData, deletePressNewsData } from '../../Services/PressManagement/PressApi';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetPressNewsData = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const [newsData, setNewsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchNewsData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllPressNewsData();
            if (response && response.success) {
                setNewsData(response.data || []);
            } else {
                setError("Failed to load press news data.");
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch press news data. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNewsData();
    }, [fetchNewsData]);

    const handleAddClick = () => {
        navigate('/press-management/news/add');
    };

    const handleEditClick = (news) => {
        navigate('/press-management/news/edit', { state: { news } });
    };

    const handleDeleteClick = async (id) => {
        if (window.confirm("Are you sure you want to delete this news item?")) {
            try {
                await deletePressNewsData(id);
                fetchNewsData(); // Refresh data
                showSnackbar("News item deleted successfully!", "success");
            } catch (err) {
                console.error(err);
                showSnackbar("Failed to delete news item.", "error");
            }
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>

            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                <IconButton onClick={() => navigate('/press-management')} sx={{ color: '#444' }}>
                    <ArrowLeft size={24} />
                </IconButton>
                <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500, flexGrow: 1 }}>
                    Press News Management
                </Typography>
                <Button disableFocusRipple disableRipple
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={handleAddClick}
                    disableElevation
                    sx={{
                        bgcolor: '#FFCC59',
                        color: '#000',
                        borderRadius: '20px',
                        px: 3, py: 1,
                        textTransform: 'none',

                    }}
                >
                    Add News
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 950 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap', textAlign: 'center' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Link</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Updated At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                        <CircularProgress sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : newsData.length > 0 ? (
                                newsData.map((item, idx) => (
                                    <TableRow key={item.id || item._id} >
                                        <TableCell sx={{ textAlign: 'center' }}>{idx + 1}</TableCell>
                                        <TableCell>
                                            <Avatar
                                                src={item.image}
                                                variant="rounded"
                                                sx={{ width: 60, height: 60, border: '1px solid #EEE' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {item.title}
                                        </TableCell>
                                        {/* <TableCell>
                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '12px',
                                                    display: 'inline-block',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    bgcolor: item.status ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                                    color: item.status ? '#4CAF50' : '#F44336'
                                                }}
                                            >
                                                {item.status ? 'Active' : 'Inactive'}
                                            </Box>
                                        </TableCell> */}
                                        <TableCell>
                                            <a href={item.link} target="_blank" rel="noopener noreferrer">
                                                {item.link}
                                            </a>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {item.cuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {item.uuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(item.createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(item.updatedAt)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleEditClick(item)}>
                                                        <SquarePen size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(item.id || item._id)}>
                                                        <Trash2 size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                        No news items found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default GetPressNewsData;
